import React from 'react';

/**
 * Brief Jun 17 §1 — Global React Error Boundary.
 *
 * Catches any ReferenceError / TypeError / etc. thrown during render in the
 * descendant tree and renders a sober fallback UI instead of a blank page
 * (cf. white-page crash on Steve's profile after Phase A deploy).
 *
 * NB : does not catch async errors (fetch/axios) — those are handled by the
 * regular try/catch + sonner toasts. Only render-time exceptions.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render exception caught', {
      message: error?.message,
      stack: error?.stack,
      component_stack: info?.componentStack,
    });
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (e) {
      // last-resort fallback
      window.location.href = '/';
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const message = this.state.error?.message || '';
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 p-6"
      >
        <div className="text-center max-w-md">
          <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg">
            <span className="text-2xl font-bold text-white">OS</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            OpenSapiens
          </h1>
          <p className="text-base text-gray-700 dark:text-gray-200 mb-1">
            Une erreur est survenue.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Réessaie en rechargeant la page ou contacte{' '}
            <a
              href="mailto:info@opensapiens.ch"
              className="text-violet-600 dark:text-violet-400 hover:underline"
            >
              info@opensapiens.ch
            </a>
            .
          </p>
          <button
            type="button"
            data-testid="error-boundary-reload-btn"
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 transition-colors"
          >
            Recharger la page
          </button>
          {message && (
            <details className="mt-6 text-left text-xs text-gray-400 dark:text-gray-500">
              <summary className="cursor-pointer">Détails techniques</summary>
              <pre className="mt-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-x-auto whitespace-pre-wrap break-words">
                {message}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
